import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {DxHeadNavigationMenuOptions} from "../../../../domain/ui-options/dx-navigator/dx-head-navigation-menu-options";
import {DxAppEvent} from "../../../../../dx-core/domain/events/dx-app-event";
import {DxNavigationTreeNodeDataProvider} from "../../../../domain/navigator/dx-navigation-tree-node-data-provider";
import {DxNavigationTree} from "../../../../domain/navigator/dx-navigation-tree";
import {DxNavigationTreeNode} from "../../../../domain/navigator/dx-navigation-tree-node";


/** Верхняя навигационная панель с поддержкй режима карусели. */
@Component({
  selector: 'dx-head-navigation-menu',
  templateUrl: './dx-head-navigation-menu.component.html',
  styleUrls: ['./dx-head-navigation-menu.component.css']
})
export class DxHeadNavigationMenuComponent implements OnInit, OnDestroy {
  /** Опции (настройки) верхнего навигационного меню */
  @Input()
  public options: DxHeadNavigationMenuOptions;

  /** Массив (плоская иерархия) объектов данных, являющихся исходными для построения навигационного дерева */
  @Input()
  public menuData: DxNavigationTreeNodeDataProvider[];

  /** Флаг, управляющий видимосьтью кнопки "Home" */
  public showHomeBtn: boolean = false;
  /** Иконка кнопки "Home". С точки зрения здравого смысла должны быть преусмотрены иконки по умолчанию. */
  public homeBtnIcon: string;
  /** Иконка кнопки "Previous" */
  public prevBtnIcon: string;
  /** Иконка кнопки "Next" */
  public nextBtnIcon: string;
  /** Флаг, управляющий видимостью карусели из пунктов меню */
  private _showCarousel: boolean = false;


  // Структуры данных для работы меню
  /** Лимит отображаемых элементов меню. */
  private _maxCountOfDisplayedItems: number = 7;
  /** Навигационное дерево */
  private _menuTree: DxNavigationTree;
  /** Массив всех нод первого уровня */
  private _items: DxNavigationTreeNode[];
  /** Массив нод первого уровня отображаемых в рамках лимита `_maxCountOfDisplayedItems` в шапке */
  private _displayedItems: DxNavigationTreeNode[];
  /** Текущий активный элемент меню (Сбрасывается при нажатии на кнопку Home) */
  private _activeItem: DxNavigationTreeNode;


  /** Событие выбрасывается при клике по кнопке Home. */
  @Output()
  public homeClick: EventEmitter<DxAppEvent> = new EventEmitter();

  /**
   * Событие выбрасывается при клике по пункту меню в шапке,
   * если данный пункт имеет иерархию подразделов.
   */
  @Output()
  public openSubMenu: EventEmitter<DxAppEvent> = new EventEmitter();

  /**
   * Событие выбрасывается при клике по пункту меню в шапке,
   * если данный пункт НЕ имеет иерархию подразделов.
   * То есть с этим пунктом ассоциирован роут, который нужно открыть в области контента.
   */
  @Output()
  public openMenuItem: EventEmitter<DxAppEvent> = new EventEmitter();


  /** @constructor */
  constructor() {
    this._items = [];
    this._displayedItems = [];
  }


  ngOnInit(): void {
    if(this.options) {
      this.showHomeBtn = this.options.showHomeBtn;
      this.homeBtnIcon = this.options.homeBtnIcon;
      this.prevBtnIcon = this.options.prevBtnIcon;
      this.nextBtnIcon = this.options.nextBtnIcon;
      this._maxCountOfDisplayedItems = this.options.maxCountOfDisplayedItems;
    }
    this._init();
  }

  ngOnDestroy(): void { }


  get menuTree(): DxNavigationTree {
    return this._menuTree;
  }

  get displayedItems(): DxNavigationTreeNode[] {
    return this._displayedItems;
  }

  get showCarousel(): boolean {
    return this._showCarousel;
  }


  public onMenuItemClick(item: DxNavigationTreeNode): void {
    this._activeItem = item;
    if(item.hasSubTree) {
      this.openSubMenu.emit(new DxAppEvent('dx-head-navigation-menu', item));
    } else {
      this.openMenuItem.emit(new DxAppEvent('dx-head-navigation-menu', item));
    }
  }


  public onHomeClick(): void {
    this._activeItem = null;
    this._displayedItems = [...this._displayedItems];
    this.homeClick.emit(new DxAppEvent('dx-head-navigation-menu'));
  }


  public unselect(): void {
    this._activeItem = null;
    this._displayedItems = [...this._displayedItems];
  }


  public restoreSelection(segments: string[]): DxNavigationTreeNode {
    // 1. Восстанавливаем отмеку на первом уровне иерархии
    const node = this._items.find(item => segments.indexOf(item.route) !== -1);
    this._activeItem = node;
    // 2. Если мы имеем более чем один уровень верхнего меню,
    //    то нужно написать логику восстановления отметки.
    if(this._menuTree.depth > 1) {
      // TODO: Дописать восстановление отмеки в подуровнях верхнего меню
    }
    return node;
  }


  public isActiveItem(item: DxNavigationTreeNode): boolean {
    if(this._activeItem) {
      return this._activeItem.id === item.id;
    }
    return false;
  }


  private _init(): void {
    this._menuTree = new DxNavigationTree(this.menuData);
    this._menuTree.grow('parentId');
    this._items = this._menuTree.collectNodesByLevel(1);

    // Если количество элементов первого уровня превышает порог
    // Активируем карусель
    if(this._items.length >= this._maxCountOfDisplayedItems) {
      this._displayedItems = this._items.slice(0, this._maxCountOfDisplayedItems);
      this._showCarousel = true;
    } else {
      this._showCarousel = false;
      this._displayedItems = [...this._items];
    }
  }


  /**
   * Вычисляет можно ли прокрутить элементы назад.
   * Смысл такой. Если начало массива `_displayedItems` совпадает с началом массива `_items`,
   * то дальше листать некуда. Лочим стрелку.
   */
  public canPrev(): boolean {
    const firstItem = this._items[0];
    const firstDisplayedItem = this._displayedItems[0];
    return firstDisplayedItem.id !== firstItem.id;
  }

  /**
   * Вычисляет можно ли прокрутить элементы назад.
   * Смысл такой. Если конец массива `_displayedItems` совпадает с концом массива `_items`,
   * то дальше листать некуда. Лочим стрелку.
   */
  public canNext(): boolean {
    const lastItem = this._items[this._items.length - 1];
    const lastDisplayedItem = this._displayedItems[this._displayedItems.length - 1];
    return lastDisplayedItem.id !== lastItem.id;
  }

  /** Проматывает список элементов меню на один элемент назад */
  public onGoPrev(): void {
    if(!this.canPrev()) return;
    // Получаем индекс текущего первого отображаемого элемента
    const index = this._items.indexOf(this._displayedItems[0]);
    if(index != -1) {
      // Находим предыдущий элемент
      const prepItem = this._items[index - 1];
      // Выкидываем из массива отображаемых элементов последний
      const remainingItems = this._displayedItems.splice(0, this._displayedItems.length - 1);
      // Склеиваем все вместе и обновляем массив
      this._displayedItems = [prepItem].concat(remainingItems);
    }
  }

  /** Проматывает список элементов меню на один элемент вперед */
  public onGoNext(): void {
    if(!this.canNext()) return;
    // Получаем индекс текущего последнего отображаемого элемента
    const index = this._items.indexOf(this._displayedItems[this._displayedItems.length - 1]);
    if(index != -1) {
      // Находим следующий элемент
      const nextItem = this._items[index + 1];
      // Выкидываем из массива отображаемых элементов первый
      const remainingItems = this._displayedItems.splice(1, this._displayedItems.length);
      remainingItems.push(nextItem);
      this._displayedItems = remainingItems;
    }
  }
}
